import React, { useState, useEffect, useRef } from 'react';
import { FileText, Star, Lock, Trash2, Check, X } from 'lucide-react';
import { ABLE_COLORS, CONTEXT_OPTIONS } from '../../../constants';
import AnimatedCheckbox from '../../AnimatedCheckbox';
import { isTaskBlocked } from '../../../utils/taskHelpers';

// Priority options for dropdown
const PRIORITY_OPTIONS = ['Now', 'Next', 'Later', 'Someday', 'Waiting', 'Scheduled', 'Unprioritized'];

/**
 * Enhanced task item for Plan mode list
 * Shows more context at a glance: priority, project/area, notes indicator, blocked status
 * Supports inline editing when expanded
 */
export default function TaskItem({ 
  task, 
  tasks, 
  isEditing,
  onStartEdit,
  onCancelEdit,
  onMarkDone, 
  onMarkUndone, 
  onToggleStar,
  onUpdateTask,
  onDeleteTask,
  contextOptions
}) {
  const blocked = isTaskBlocked(task, tasks);
  const titleInputRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Edit buffer for inline editing
  const [editBuffer, setEditBuffer] = useState({
    title: task.title,
    priority: task.priority || 'Unprioritized',
    project: task.project || '',
    area: task.area || '',
    notes: task.notes || ''
  });

  // Reset edit buffer when task changes or editing starts
  useEffect(() => {
    if (isEditing) {
      setEditBuffer({
        title: task.title,
        priority: task.priority || 'Unprioritized',
        project: task.project || '',
        area: task.area || '',
        notes: task.notes || ''
      });
      // Focus title input
      setTimeout(() => titleInputRef.current?.focus(), 50);
    }
  }, [isEditing, task]);

  // Get priority color from design tokens
  const getPriorityColor = (priority) => {
    const key = priority?.toLowerCase();
    return ABLE_COLORS.priority[key] || ABLE_COLORS.textMuted;
  };

  const priorityColor = getPriorityColor(task.priority);
  const showPriority = task.priority && task.priority !== 'Unprioritized';

  const handleSave = () => {
    const updates = {};
    
    if (editBuffer.title.trim() && editBuffer.title !== task.title) {
      updates.title = editBuffer.title.trim();
    }
    if (editBuffer.priority !== task.priority) {
      updates.priority = editBuffer.priority;
    }
    if (editBuffer.project !== (task.project || '')) {
      updates.project = editBuffer.project || null;
    }
    if (editBuffer.area !== (task.area || '')) {
      updates.area = editBuffer.area || null;
    }
    if (editBuffer.notes !== (task.notes || '')) {
      updates.notes = editBuffer.notes;
    }
    
    if (Object.keys(updates).length > 0) {
      onUpdateTask(task.id, updates);
    }
    onCancelEdit();
  };

  const handleDelete = () => {
    onDeleteTask(task.id);
    setShowDeleteConfirm(false);
    onCancelEdit();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      onCancelEdit();
    }
  };

  // Editing mode
  if (isEditing) {
    return (
      <div style={{
        padding: '14px',
        background: ABLE_COLORS.bgSubtle,
        borderRadius: ABLE_COLORS.chipRadius,
        border: `2px solid ${ABLE_COLORS.primary}`,
        boxSizing: 'border-box',
        width: '100%'
      }}>
        {/* Title input */}
        <input
          ref={titleInputRef}
          type="text"
          value={editBuffer.title}
          onChange={(e) => setEditBuffer(prev => ({ ...prev, title: e.target.value }))}
          onKeyDown={handleKeyDown}
          placeholder="Task title..."
          style={{
            width: '100%',
            padding: '10px 12px',
            border: `1px solid ${ABLE_COLORS.border}`,
            borderRadius: ABLE_COLORS.radiusSmall,
            fontSize: '14px',
            fontWeight: '500',
            outline: 'none',
            marginBottom: '12px',
            boxSizing: 'border-box'
          }}
        />
        
        {/* Priority and Area row */}
        <div className="task-edit-row" style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {/* Priority dropdown */}
          <div className="task-edit-field" style={{ flex: 1, minWidth: '120px' }}>
            <label style={{ 
              fontSize: '11px', 
              fontWeight: '600', 
              color: ABLE_COLORS.textMuted,
              display: 'block',
              marginBottom: '4px'
            }}>
              Priority
            </label>
            <select
              value={editBuffer.priority}
              onChange={(e) => setEditBuffer(prev => ({ ...prev, priority: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusSmall,
                fontSize: '13px',
                outline: 'none',
                background: ABLE_COLORS.neutral,
                cursor: 'pointer'
              }}
            >
              {PRIORITY_OPTIONS.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          
          {/* Area dropdown */}
          <div className="task-edit-field" style={{ flex: 1, minWidth: '120px' }}>
            <label style={{ 
              fontSize: '11px', 
              fontWeight: '600', 
              color: ABLE_COLORS.textMuted,
              display: 'block',
              marginBottom: '4px'
            }}>
              Area
            </label>
            <select
              value={editBuffer.area}
              onChange={(e) => setEditBuffer(prev => ({ ...prev, area: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusSmall,
                fontSize: '13px',
                outline: 'none',
                background: ABLE_COLORS.neutral,
                cursor: 'pointer'
              }}
            >
              <option value="">No area</option>
              {CONTEXT_OPTIONS.area.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
        </div>
        <style>{`
          @media (max-width: 360px) {
            .task-edit-field { flex-basis: 100% !important; }
          }
        `}</style>
        
        {/* Project input */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            fontSize: '11px', 
            fontWeight: '600', 
            color: ABLE_COLORS.textMuted,
            display: 'block',
            marginBottom: '4px'
          }}>
            Project
          </label>
          <input
            type="text"
            value={editBuffer.project}
            onChange={(e) => setEditBuffer(prev => ({ ...prev, project: e.target.value }))}
            onKeyDown={handleKeyDown}
            placeholder="Project name (optional)"
            list="project-suggestions"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: ABLE_COLORS.radiusSmall,
              fontSize: '13px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <datalist id="project-suggestions">
            {contextOptions.project?.map(p => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </div>
        
        {/* Notes textarea */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            fontSize: '11px', 
            fontWeight: '600', 
            color: ABLE_COLORS.textMuted,
            display: 'block',
            marginBottom: '4px'
          }}>
            Notes
          </label>
          <textarea
            value={editBuffer.notes}
            onChange={(e) => setEditBuffer(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Add notes..."
            rows={3}
            style={{
              width: '100%',
              padding: '8px 10px',
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: ABLE_COLORS.radiusSmall,
              fontSize: '13px',
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box'
            }}
          />
        </div>
        
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
          {/* Delete button */}
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                padding: '8px 12px',
                background: 'transparent',
                border: `1px solid ${ABLE_COLORS.semantic.error}40`,
                borderRadius: ABLE_COLORS.radiusSmall,
                cursor: 'pointer',
                color: ABLE_COLORS.semantic.error,
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Trash2 size={14} />
              Delete
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: ABLE_COLORS.semantic.error }}>Delete?</span>
              <button
                onClick={handleDelete}
                style={{
                  padding: '6px 10px',
                  background: ABLE_COLORS.semantic.error,
                  border: 'none',
                  borderRadius: ABLE_COLORS.radiusSmall,
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                Yes
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '6px 10px',
                  background: ABLE_COLORS.bgSubtle,
                  border: `1px solid ${ABLE_COLORS.border}`,
                  borderRadius: ABLE_COLORS.radiusSmall,
                  cursor: 'pointer',
                  color: ABLE_COLORS.textSecondary,
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                No
              </button>
            </div>
          )}
          
          {/* Save/Cancel buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={onCancelEdit}
              style={{
                padding: '8px 14px',
                background: ABLE_COLORS.neutral,
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusSmall,
                cursor: 'pointer',
                color: ABLE_COLORS.textSecondary,
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <X size={14} />
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: '8px 14px',
                background: ABLE_COLORS.primary,
                border: 'none',
                borderRadius: ABLE_COLORS.radiusSmall,
                cursor: 'pointer',
                color: 'white',
                fontSize: '13px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Check size={14} />
              Save
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal display mode
  return (
    <div 
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '14px',
        boxSizing: 'border-box',
        width: '100%',
        background: blocked
          ? ABLE_COLORS.semantic.blockedBg
          : task.starred
            ? `${ABLE_COLORS.warm}06`
            : ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.chipRadius,
        border: `1px solid ${
          blocked
            ? `${ABLE_COLORS.semantic.blocked}30`
            : task.starred
              ? `${ABLE_COLORS.warm}25`
              : ABLE_COLORS.borderLight
        }`,
        // Left accent border for priority
        borderLeft: showPriority
          ? `3px solid ${priorityColor}`
          : `1px solid ${blocked ? `${ABLE_COLORS.semantic.blocked}30` : task.starred ? `${ABLE_COLORS.warm}25` : ABLE_COLORS.borderLight}`,
        opacity: blocked ? 0.85 : 1,
        transition: 'all 0.15s ease',
        cursor: 'pointer'
      }}
      onClick={() => !blocked && onStartEdit()}
    >
      {/* Animated Checkbox */}
      <div 
        style={{ paddingTop: '1px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <AnimatedCheckbox
          checked={task.done}
          onChange={() => task.done ? onMarkUndone(task.id) : onMarkDone(task.id)}
          size={20}
          disabled={blocked}
        />
      </div>

      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Title row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: task.project || task.area || blocked ? '6px' : 0
        }}>
          {/* Blocked indicator */}
          {blocked && (
            <Lock
              size={14}
              color={ABLE_COLORS.semantic.blocked}
              style={{ flexShrink: 0 }}
            />
          )}

          {/* Title */}
          <span style={{
            fontSize: '14px',
            fontWeight: '500',
            color: blocked ? ABLE_COLORS.semantic.blocked : ABLE_COLORS.textPrimary,
            textDecoration: task.done ? 'line-through' : 'none',
            opacity: task.done ? 0.6 : 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1
          }}>
            {task.title}
          </span>

          {/* Notes indicator */}
          {task.notes && (
            <FileText
              size={14}
              color={ABLE_COLORS.textMuted}
              style={{ flexShrink: 0 }}
              title="Has notes"
            />
          )}
        </div>

        {/* Context row - project, area, blocked by */}
        {(task.project || task.area || blocked) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexWrap: 'wrap'
          }}>
            {/* Project chip */}
            {task.project && (
              <span style={{
                fontSize: '11px',
                fontWeight: '500',
                padding: '2px 8px',
                borderRadius: '6px',
                background: `${ABLE_COLORS.primary}10`,
                color: ABLE_COLORS.primary
              }}>
                {task.project}
              </span>
            )}

            {/* Area chip */}
            {task.area && (
              <span style={{
                fontSize: '11px',
                fontWeight: '500',
                padding: '2px 8px',
                borderRadius: '6px',
                background: `${ABLE_COLORS.textMuted}15`,
                color: ABLE_COLORS.textSecondary
              }}>
                {task.area}
              </span>
            )}

            {/* Blocked by indicator */}
            {blocked && (
              <span style={{
                fontSize: '11px',
                fontWeight: '500',
                color: ABLE_COLORS.semantic.blocked
              }}>
                Blocked
              </span>
            )}
          </div>
        )}
      </div>

      {/* Right side - priority badge and star */}
      <div 
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Priority badge */}
        {showPriority && task.priority !== 'Later' && (
          <span style={{
            fontSize: '10px',
            fontWeight: '600',
            padding: '3px 8px',
            borderRadius: '6px',
            background: `${priorityColor}15`,
            color: priorityColor,
            textTransform: 'uppercase',
            letterSpacing: '0.3px'
          }}>
            {task.priority}
          </span>
        )}

        {/* Star button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleStar(task.id);
          }}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = `${ABLE_COLORS.warm}15`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <Star
            size={16}
            fill={task.starred ? ABLE_COLORS.warm : 'none'}
            color={task.starred ? ABLE_COLORS.warm : ABLE_COLORS.textMuted}
          />
        </button>
      </div>
    </div>
  );
}
